import { Injectable, inject } from '@angular/core';

import { Client } from 'src/providers/client/client';

import { AddressListing } from 'src/shared/services/marketing-channels.service';

import { DIRECT_BOOKING_PLATFORM, STR_CHANNEL_PLATFORMS, formatPlatformName, getConnectItemTitle } from 'src/shared/constants/listing-platforms';
import { CONCIERGE_SETUP_STATUS, NEEDS_SETUP_STATUS } from 'src/pages/property-listings/property-listings.status';

// What the client told us about one address × channel through Tell Us About Your
// Listings. Only "no listing — TIDY sets it up" needs recording (a "yes" IS the
// backend listing row); `none` clears a marker after they later add the listing.
export type ListingSetupAnswer = 'concierge' | 'none';

export interface AddressListingSetupState {
  addressId: number;
  // Channels the client said they don't have a listing on (so TIDY creates one).
  conciergePlatforms: Set<string>;
  // The address's "Tell Us About Your Listings" action item is done.
  listingsStepCompleted: boolean;
}

// One of TIDY's action items as c.tidy stores them (concierge-v2 `ConciergeActionItem`),
// in customer metadata `concierge_action_items`.
export interface ConciergeActionItem {
  id: string;
  key: string;
  title: string;
  detail: string;
  source: string;
  createdAt: string;
  createdBy: string;
  doneAt: string | null;
  doneBy: string | null;
  hiddenAt?: string | null;
}

export interface NewChannelListing {
  id: number;
  platform: string;
  addressName: string;
}

interface ActionItemEntry {
  key: string;
  title: string;
  detail: string;
}

// A channel the client said they have no listing on, so TIDY creates one.
export interface ListingToCreate {
  addressId: number;
  platform: string;
  addressName: string;
}

/**
 * Backing service for the Tell Us About Your Listings paradigm.
 *
 * The Listings page always shows Airbnb / VRBO / Booking.com for every address. The
 * absence of a backend listing is read as "the client hasn't told us yet" (needs
 * setup) unless they answered "I don't have one", which is recorded as customer
 * metadata `onboarding_step.{addressId}.listing_setup.{platform} = concierge` and
 * read back as "concierge is setting it up". The dotted suffix keeps the key out of
 * the concierge board's generic `onboarding_step.{id}.{stepId}` matcher.
 *
 * Completing the flow writes the same `onboarding_step.{addressId}.listings =
 * completed` row the action-item surfaces and the concierge board already read.
 */
@Injectable({ providedIn: 'root' })
export class ListingSetupService {
  static readonly LISTINGS_STEP_ID = 'listings';
  static readonly COHOST_EMAIL = 'pm@tidy.com';
  static readonly ACTION_ITEMS_KEY = 'concierge_action_items';

  readonly platforms: readonly string[] = STR_CHANNEL_PLATFORMS;

  private client = inject(Client);
  private actionItemsQueue: Promise<void> = Promise.resolve();

  // Reads the setup markers + step completion for every address in one sweep. Chunked
  // to keep the keys[]= query string short (4 keys per address).
  async getSetupState(addressIds: number[]): Promise<Map<number, AddressListingSetupState>> {
    const state = new Map<number, AddressListingSetupState>();
    for (const id of addressIds) {
      state.set(id, { addressId: id, conciergePlatforms: new Set<string>(), listingsStepCompleted: false });
    }
    if (!addressIds.length) { return state; }
    for (const chunk of this.chunk(addressIds, 18)) {
      const keys: string[] = [];
      for (const id of chunk) {
        keys.push(`external.${this.stepKey(id)}`);
        for (const platform of this.platforms) { keys.push(`external.${this.markerKey(id, platform)}`); }
      }
      let rows: any[] = [];
      try {
        rows = await this.client.getMetadata(keys);
      } catch {
        rows = [];
      }
      for (const row of (Array.isArray(rows) ? rows : [])) {
        const key = String(row?.key || '').replace(/^external\./, '');
        const match = key.match(/^onboarding_step\.(\d+)\.(.+)$/);
        if (!match) { continue; }
        const entry = state.get(Number(match[1]));
        if (!entry) { continue; }
        if (match[2] === ListingSetupService.LISTINGS_STEP_ID) {
          entry.listingsStepCompleted = row.value === 'completed';
        } else if (match[2].startsWith('listing_setup.') && row.value === 'concierge') {
          entry.conciergePlatforms.add(match[2].slice('listing_setup.'.length));
        }
      }
    }
    return state;
  }

  markConciergeSetup(addressId: number, platform: string): Promise<any> {
    return this.writeMarker(addressId, platform, 'concierge');
  }

  clearConciergeSetup(addressId: number, platform: string): Promise<any> {
    return this.writeMarker(addressId, platform, 'none');
  }

  // Flips the address's "Tell Us About Your Listings" action item to done.
  async markListingsStepCompleted(addressIds: number[]): Promise<void> {
    for (const id of addressIds) {
      await this.client.addMetadata({ key: this.stepKey(id), value: 'completed' });
    }
  }

  // Puts what TIDY has to do to connect a channel listing the client just created on
  // TIDY's action items in c.tidy: the Vrbo re-create or Booking.com transfer, or making
  // sure the Airbnb co-host invite lands. Never throws.
  addListingConnectItem(listing: NewChannelListing): Promise<void> {
    return this.addActionItems(this.connectEntries([listing])).catch((err) => {
      console.error('Could not add the listing to TIDY action items', err);
    });
  }

  // Puts one save's connect items and its "Create a <Platform> listing" items (for each
  // channel the client said they have no listing on) on TIDY's action items in one read
  // and one write. Rejects when the list could not be read or written, so the caller
  // can stop before it records that TIDY is on those listings.
  addListingSetupItems(connect: NewChannelListing[], create: ListingToCreate[]): Promise<void> {
    return this.addActionItems([...this.connectEntries(connect), ...this.createEntries(create)]);
  }

  private connectEntries(listings: NewChannelListing[]): ActionItemEntry[] {
    return (listings || [])
      .filter((listing) => listing?.id && this.isChannel(listing.platform))
      .map((listing) => ({
        key: `listing_connect:${listing.id}`,
        title: getConnectItemTitle(listing.platform),
        detail: [listing.addressName, formatPlatformName(listing.platform)].filter(Boolean).join(' · '),
      }));
  }

  private createEntries(listings: ListingToCreate[]): ActionItemEntry[] {
    return (listings || [])
      .filter((listing) => listing?.addressId && this.isChannel(listing.platform))
      .map((listing) => {
        const platformName = formatPlatformName(listing.platform);
        return {
          key: `listing_create:${listing.addressId}:${listing.platform}`,
          title: `Create a ${platformName} listing`,
          detail: [listing.addressName, platformName].filter(Boolean).join(' · '),
        };
      });
  }

  // Writes nothing unless the current list was read and is a JSON array: existing items
  // are kept as read, the new ones are appended, and a key that already has its item is
  // left alone. Calls run one after another, so items added in one save are all kept,
  // and a call that failed does not stop the next one.
  private addActionItems(entries: ActionItemEntry[]): Promise<void> {
    if (!entries.length) { return Promise.resolve(); }
    const step = this.actionItemsQueue.then(() => this.appendActionItems(entries));
    this.actionItemsQueue = step.catch(() => undefined);
    return step;
  }

  private async appendActionItems(entries: ActionItemEntry[]): Promise<void> {
    const rows = await this.client.getMetadata([`external.${ListingSetupService.ACTION_ITEMS_KEY}`]);
    const items = this.parseActionItems(rows);
    if (!items) { return; }
    const keys = new Set(items.map((item) => item?.key));
    const added: ConciergeActionItem[] = [];
    for (const { key, title, detail } of entries) {
      if (keys.has(key)) { continue; }
      keys.add(key);
      added.push({
        id: `ci-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        key,
        title,
        detail,
        source: 'Client app',
        createdAt: new Date().toISOString(),
        createdBy: 'Client app',
        doneAt: null,
        doneBy: null,
        hiddenAt: null,
      });
    }
    if (!added.length) { return; }
    await this.client.addMetadata({
      key: ListingSetupService.ACTION_ITEMS_KEY,
      value: JSON.stringify([...items, ...added]),
    });
  }

  private isChannel(platform: string): boolean {
    return (this.platforms as readonly string[]).includes(platform);
  }

  // The stored list, [] when the client has none yet, or null when the response or the
  // stored value is anything else (then nothing may be written over it).
  private parseActionItems(rows: any): any[] | null {
    if (!Array.isArray(rows)) { return null; }
    const row = rows.find((r) => String(r?.key || '').replace(/^external\./, '') === ListingSetupService.ACTION_ITEMS_KEY);
    const raw = row?.value;
    if (raw === undefined || raw === null || raw === '') { return []; }
    if (typeof raw !== 'string') { return null; }
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  // Every address × short-term channel with no backend listing becomes a synthetic
  // row, grouped under its address in a fixed platform order so the page reads the
  // same for every account. Real rows keep their data; mock room rows trail.
  withPlaceholderRows(
    addresses: any[],
    listings: AddressListing[],
    state: Map<number, AddressListingSetupState>,
  ): AddressListing[] {
    const rows: AddressListing[] = [];
    for (const address of addresses || []) {
      const forAddress = listings.filter((l) => l.address_id === address.id && !l.isMockRoomListing);
      for (const platform of this.platforms) {
        const real = forAddress.filter((l) => l.platform === platform);
        if (real.length) {
          rows.push(...real);
          continue;
        }
        const markers = state.get(address.id)?.conciergePlatforms;
        rows.push({
          id: this.placeholderId(address.id, platform),
          address_id: address.id,
          platform,
          platform_id: '',
          status: markers?.has(platform) ? CONCIERGE_SETUP_STATUS : NEEDS_SETUP_STATUS,
          created_at: '',
          updated_at: '',
          address,
          isPlaceholder: true,
        });
      }
      rows.push(...forAddress.filter((l) => l.platform === DIRECT_BOOKING_PLATFORM));
      rows.push(...forAddress.filter((l) => !this.platforms.includes(l.platform) && l.platform !== DIRECT_BOOKING_PLATFORM));
    }
    rows.push(...listings.filter((l) => l.isMockRoomListing));
    return rows;
  }

  // Negative so it can never collide with a backend address_listing id.
  placeholderId(addressId: number, platform: string): number {
    return -(addressId * 10 + this.platforms.indexOf(platform) + 1);
  }

  private writeMarker(addressId: number, platform: string, value: ListingSetupAnswer): Promise<any> {
    return this.client.addMetadata({ key: this.markerKey(addressId, platform), value });
  }

  private markerKey(addressId: number, platform: string): string {
    return `onboarding_step.${addressId}.listing_setup.${platform}`;
  }

  private stepKey(addressId: number): string {
    return `onboarding_step.${addressId}.${ListingSetupService.LISTINGS_STEP_ID}`;
  }

  private chunk<T>(arr: T[], size: number): T[][] {
    const out: T[][] = [];
    for (let i = 0; i < arr.length; i += size) { out.push(arr.slice(i, i + size)); }
    return out;
  }
}
